import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { ChannelNamingUtil } from "@semo/shared-chat";
import {
  getNotificationsControllerGetNotificationsQueryKey,
  getNotificationsControllerGetUnreadCountQueryKey,
} from "@semo/api-contracts";
import { toast } from "sonner";

/**
 * Global Realtime hook for Notifications
 *
 * Subscribes to the supplier's notification channel to receive:
 * 1. New notification events
 * 2. Unread count update events
 */
export function useGlobalNotificationRealtime(currentUserId?: string) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!currentUserId || !supabase) return;

    const channelName =
      ChannelNamingUtil.getSupplierNotificationChannel(currentUserId);
    console.log(
      `[useGlobalNotificationRealtime] Subscribing to ${channelName}`,
    );

    const channel = supabase
      .channel(`${channelName}_notif`) // Append suffix to prevent conflict if same channel name used differently, though Supabase merges channels with same name under the hood. Better to use the same channel connection, but `supabase.channel()` with the same name returns the existing channel if it's already created? Actually `supabase.channel` creates a new instance if we use a different name, but for Postgres it matters. For broadcast, it's just topic name.
      .on("broadcast", { event: "new-notification" }, (event) => {
        console.log(
          "[useGlobalNotificationRealtime] New notification received:",
          event,
        );

        // Invalidate queries to refresh notification list and badge
        queryClient.invalidateQueries({
          queryKey: getNotificationsControllerGetNotificationsQueryKey(),
        });
        queryClient.invalidateQueries({
          queryKey: getNotificationsControllerGetUnreadCountQueryKey(),
        });

        const payload = event.payload;
        if (payload?.notification) {
          toast.info(payload.notification.title, {
            description: payload.notification.message,
          });
        }
      })
      .on("broadcast", { event: "unread-count-updated" }, (event) => {
        console.log(
          "[useGlobalNotificationRealtime] Unread count updated:",
          event,
        );

        // Invalidate to fetch latest count
        queryClient.invalidateQueries({
          queryKey: getNotificationsControllerGetUnreadCountQueryKey(),
        });
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentUserId, queryClient]);
}
